import React, { useEffect } from 'react';

import { useSimpleLoyaltyList } from 'api/simple';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { TOrbit } from '../../../i18n/TModules';
import PropertyConditions from '../kit/PropertyConditions';
import { Checkbox, ChipGroup, RadioGroup } from '../kit/controls';
import {
  AppTriggerSelect,
  DateField,
  SelectField,
  TextField,
  TransactionTypeField
} from '../kit/fields';
import { FieldRow, Form, Hint, Line, Sub, Words } from '../kit/layout';
import { CampaignPicker, RewardPicker } from '../kit/pickers';
import {
  CAMPAIGN_ACTIVITIES,
  CampaignActivity,
  MONTHS,
  WEEK_DAYS,
  WizardCampaignType,
  WizardTrigger
} from '../wizardModel';

const TT = `${TOrbit}.wizard.trigger`;

const label = (key: string) => key.charAt(0).toUpperCase() + key.slice(1);

interface ITriggerFormProps {
  type: WizardCampaignType;
  trigger: WizardTrigger;
  onChange: (patch: Partial<WizardTrigger>) => void;
}

/** 6778:18228 (Stamp) / 6778:23596 (Progress): the transaction sentence, line by line. */
const TransactionFields: React.FC<ITriggerFormProps> = ({ trigger, onChange }) => {
  const { t } = useMultiLanguageTranslation();
  const TX = `${TT}.transaction`;
  return (
    <>
      <Line>
        <Checkbox
          checked={trigger.amountOn}
          label={t(`${TX}.amount`)}
          onChange={amountOn => onChange({ amountOn })}
        />
        <Words $muted={!trigger.amountOn}>{t(`${TX}.amount`)}</Words>
        <TextField
          label={t(`${TX}.amount`)}
          placeholder="0.00"
          inputMode="decimal"
          value={trigger.amount}
          disabled={!trigger.amountOn}
          onChange={amount => onChange({ amount })}
        />
        <Hint>{t(`${TX}.amountHint`)}</Hint>
      </Line>
      <Line>
        <Checkbox
          checked={trigger.typeOn}
          label={t(`${TX}.type`)}
          onChange={typeOn => onChange({ typeOn })}
        />
        <Words $muted={!trigger.typeOn}>{t(`${TX}.type`)}</Words>
        <TransactionTypeField
          value={trigger.transactionType}
          disabled={!trigger.typeOn}
          onChange={transactionType => onChange({ transactionType })}
        />
        <Words $muted>{t(`${TX}.typeUnit`)}</Words>
      </Line>
      <Line>
        <Checkbox
          checked={trigger.scheduleOn}
          label={t(`${TX}.schedule`)}
          onChange={scheduleOn => onChange({ scheduleOn })}
        />
        <Words $muted={!trigger.scheduleOn}>{t(`${TX}.schedule`)}</Words>
      </Line>
      <Sub>
        <FieldRow>
          <Words $muted>{t(`${TX}.days`)}</Words>
          <ChipGroup
            label={t(`${TX}.days`)}
            value={trigger.days}
            disabled={!trigger.scheduleOn}
            options={WEEK_DAYS.map(day => ({ value: day, label: label(day) }))}
            onChange={days => onChange({ days })}
          />
        </FieldRow>
        <FieldRow>
          <Words $muted>{t(`${TX}.months`)}</Words>
          <ChipGroup
            label={t(`${TX}.months`)}
            value={trigger.months}
            disabled={!trigger.scheduleOn}
            options={MONTHS.map(month => ({ value: month, label: label(month) }))}
            onChange={months => onChange({ months })}
          />
        </FieldRow>
        <FieldRow>
          <Words $muted>{t(`${TX}.deadline`)}</Words>
          <Line>
            <DateField
              label={t(`${TX}.deadline`)}
              value={trigger.deadline}
              disabled={!trigger.scheduleOn}
              onChange={deadline => onChange({ deadline })}
            />
            <Hint>{t(`${TX}.deadlineHint`)}</Hint>
          </Line>
        </FieldRow>
      </Sub>
      <Line>
        <Checkbox
          checked={trigger.propertiesOn}
          label={t(`${TX}.properties`)}
          onChange={propertiesOn => onChange({ propertiesOn })}
        />
        <Words $muted={!trigger.propertiesOn}>{t(`${TX}.properties`)}</Words>
      </Line>
      <PropertyConditions
        value={trigger.properties}
        disabled={!trigger.propertiesOn}
        onChange={properties => onChange({ properties })}
      />
    </>
  );
};

/**
 * 6778:18595: the module stores no earn / redeem choice, minimum or properties for a points
 * trigger, so only "Earn Points" is said; the first loyalty programme is the one followed.
 */
const LoyaltyFields: React.FC<ITriggerFormProps> = ({ type, trigger, onChange }) => {
  const { t } = useMultiLanguageTranslation();
  const { data } = useSimpleLoyaltyList();
  const first = data?.[0];

  useEffect(() => {
    if (trigger.loyalty === null && first !== undefined) {
      onChange({ loyalty: { id: first.id, name: first.name } });
    }
  }, [trigger.loyalty, first, onChange]);

  return (
    <Line>
      <Words>{`${t(`${TT}.leadIn.${type}`)}:`}</Words>
      <Words>{t(`${TT}.loyalty.earn`)}</Words>
    </Line>
  );
};

/** 6778:19612: the activity, then the campaign it is about. */
const MilestoneFields: React.FC<ITriggerFormProps> = ({ type, trigger, onChange }) => {
  const { t } = useMultiLanguageTranslation();
  return (
    <>
      <Line>
        <Words>{t(`${TT}.leadIn.${type}`)}</Words>
        <SelectField<CampaignActivity>
          label={t(`${TT}.milestone.question`)}
          width="xl"
          value={trigger.activity}
          options={CAMPAIGN_ACTIVITIES.map(activity => ({
            value: activity,
            label: t(`${TT}.milestone.${activity}`)
          }))}
          onChange={activity => onChange({ activity })}
        />
      </Line>
      <Line>
        <Words>{t(`${TT}.milestone.selectCampaign`)}</Words>
        <CampaignPicker
          label={t(`${TT}.milestone.selectCampaign`)}
          value={trigger.campaign}
          onChange={campaign => onChange({ campaign })}
        />
      </Line>
    </>
  );
};

/** 6778:21082 / :21301: any reward, or a specific one. The frames' hint callouts are not shipped. */
const RewardFields: React.FC<ITriggerFormProps> = ({ type, trigger, onChange }) => {
  const { t } = useMultiLanguageTranslation();
  return (
    <>
      <Line>
        <Words>{t(`${TT}.leadIn.${type}`)}</Words>
        <SelectField<WizardTrigger['rewardActivity']>
          label={t(`${TT}.reward.question`)}
          width="xl"
          value={trigger.rewardActivity}
          options={[
            { value: 'any', label: t(`${TT}.reward.any`) },
            { value: 'specific', label: t(`${TT}.reward.specific`) }
          ]}
          onChange={rewardActivity => onChange({ rewardActivity })}
        />
      </Line>
      {trigger.rewardActivity === 'specific' ? (
        <Line>
          <Words>{t(`${TT}.reward.selected`)}</Words>
          <RewardPicker
            label={t(`${TT}.reward.selectReward`)}
            value={trigger.reward}
            onChange={reward => onChange({ reward })}
          />
        </Line>
      ) : null}
    </>
  );
};

/** 6778:21530: the tenant's custom actions, by name. */
const OthersFields: React.FC<ITriggerFormProps> = ({ type, trigger, onChange }) => {
  const { t } = useMultiLanguageTranslation();
  return (
    <Line>
      <Words>{t(`${TT}.leadIn.${type}`)}</Words>
      <AppTriggerSelect
        value={trigger.appTrigger}
        onChange={appTrigger => onChange({ appTrigger })}
      />
    </Line>
  );
};

/** 6778:23596's points basis, above a Progress rule's trigger. */
const ProgressPoints: React.FC<ITriggerFormProps> = ({ trigger, onChange }) => {
  const { t } = useMultiLanguageTranslation();
  const TP = `${TT}.progress`;
  const points = (
    <TextField
      label={t(`${TP}.${trigger.earnType}`)}
      placeholder="0"
      inputMode="numeric"
      value={trigger.points}
      onChange={next => onChange({ points: next })}
    />
  );
  return (
    <>
      <Line>
        <Words>{t(`${TP}.basis`)}</Words>
        <RadioGroup<WizardTrigger['earnType']>
          name="orbit-wizard-earn-type"
          label={t(`${TP}.basis`)}
          value={trigger.earnType}
          options={[
            { value: 'fixed', label: t(`${TP}.perTransaction`) },
            { value: 'variable', label: t(`${TP}.perAmount`) }
          ]}
          onChange={earnType => onChange({ earnType })}
        />
      </Line>
      <Sub>
        {trigger.earnType === 'fixed' ? (
          <Line>
            <Words>{t(`${TP}.fixed`)}</Words>
            {points}
            <Words $muted>{t(`${TP}.fixedUnit`)}</Words>
          </Line>
        ) : (
          <Line>
            <Words>{t(`${TP}.variable`)}</Words>
            <TextField
              label={t(`${TP}.variableSpent`)}
              placeholder="1"
              inputMode="numeric"
              value={trigger.base}
              onChange={base => onChange({ base })}
            />
            <Words>{t(`${TP}.variableSpent`)}</Words>
            {points}
            <Words>{t(`${TP}.variableCap`)}</Words>
            <TextField
              label={t(`${TP}.variableCap`)}
              placeholder="0"
              inputMode="numeric"
              value={trigger.cap}
              onChange={cap => onChange({ cap })}
            />
            <Words $muted>{t(`${TP}.variableCapUnit`)}</Words>
          </Line>
        )}
      </Sub>
    </>
  );
};

const FIELDS: Record<WizardTrigger['kind'], React.FC<ITriggerFormProps>> = {
  transaction: TransactionFields,
  loyalty: LoyaltyFields,
  milestone: MilestoneFields,
  reward: RewardFields,
  others: OthersFields
};

/** The Step 3/5 form for one trigger, by its action tile. */
const TriggerForm: React.FC<ITriggerFormProps> = props => {
  const Fields = FIELDS[props.trigger.kind];
  return (
    <Form>
      {props.type === 'progress' ? <ProgressPoints {...props} /> : null}
      <Fields {...props} />
    </Form>
  );
};

export default TriggerForm;
